/**
 * IndexedDB wrapper for offline-first checkout system
 * Stores pending transactions locally for sync when online
 */

const DB_NAME = 'shabu_pos_offline';
const DB_VERSION = 1;

const STORES = {
  PENDING_TRANSACTIONS: 'pendingTransactions',
  SYNC_HISTORY: 'syncHistory',
  CONFLICTS: 'conflicts'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initOfflineDb() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.log('[OfflineDB] Upgrading database schema...');

      // Pending transactions store
      if (!database.objectStoreNames.contains(STORES.PENDING_TRANSACTIONS)) {
        const txnStore = database.createObjectStore(STORES.PENDING_TRANSACTIONS, {
          keyPath: 'offlineId'
        });
        txnStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        txnStore.createIndex('tableCode', 'tableCode', { unique: false });
        txnStore.createIndex('createdAt', 'createdAt', { unique: false });
        console.log('[OfflineDB] Created pendingTransactions store');
      }

      // Sync history store
      if (!database.objectStoreNames.contains(STORES.SYNC_HISTORY)) {
        const historyStore = database.createObjectStore(STORES.SYNC_HISTORY, {
          keyPath: 'id',
          autoIncrement: true
        });
        historyStore.createIndex('offlineId', 'offlineId', { unique: false });
        historyStore.createIndex('syncedAt', 'syncedAt', { unique: false });
        console.log('[OfflineDB] Created syncHistory store');
      }

      // Conflicts store
      if (!database.objectStoreNames.contains(STORES.CONFLICTS)) {
        const conflictStore = database.createObjectStore(STORES.CONFLICTS, {
          keyPath: 'id',
          autoIncrement: true
        });
        conflictStore.createIndex('offlineId', 'offlineId', { unique: false });
        conflictStore.createIndex('resolvedAt', 'resolvedAt', { unique: false });
        console.log('[OfflineDB] Created conflicts store');
      }
    };
  });
}

/**
 * Get the database instance, initializing if needed
 */
async function getDb() {
  if (!db) {
    await initOfflineDb();
  }
  return db;
}

// ============================================
// PENDING TRANSACTIONS OPERATIONS
// ============================================

/**
 * Add a new pending transaction
 * @param {Object} transaction - Transaction data with offlineId
 */
export async function addPendingTransaction(transaction) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readwrite');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);

    const record = {
      ...transaction,
      syncStatus: 'pending',
      createdAt: transaction.createdAt || Date.now(),
      lastSyncAttempt: null,
      syncAttempts: 0,
      errorMessage: null,
      serverReceiptId: null,
      serverTransactionId: null,
      syncedAt: null
    };

    const request = store.add(record);

    request.onsuccess = () => {
      console.log(`[OfflineDB] Added pending transaction: ${transaction.offlineId}`);
      resolve(record);
    };

    request.onerror = () => {
      console.error('[OfflineDB] Failed to add transaction:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Get all pending transactions (status = 'pending')
 */
export async function getPendingTransactions() {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readonly');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);
    const index = store.index('syncStatus');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      // Sort by createdAt (oldest first for FIFO sync)
      const transactions = request.result.sort((a, b) => a.createdAt - b.createdAt);
      resolve(transactions);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Get all transactions (any status)
 */
export async function getAllTransactions() {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readonly');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Get a transaction by offlineId
 */
export async function getTransactionById(offlineId) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readonly');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);
    const request = store.get(offlineId);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Update a pending transaction
 * @param {string} offlineId - Transaction ID
 * @param {Object} updates - Fields to update
 */
export async function updateTransaction(offlineId, updates) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readwrite');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);
    const getRequest = store.get(offlineId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Transaction not found: ${offlineId}`));
        return;
      }

      const updated = { ...existing, ...updates };
      const putRequest = store.put(updated);

      putRequest.onsuccess = () => {
        console.log(`[OfflineDB] Updated transaction: ${offlineId}`, updates);
        resolve(updated);
      };

      putRequest.onerror = () => {
        reject(putRequest.error);
      };
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
}

/**
 * Delete a transaction by offlineId
 */
export async function deleteTransaction(offlineId) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readwrite');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);
    const request = store.delete(offlineId);

    request.onsuccess = () => {
      console.log(`[OfflineDB] Deleted transaction: ${offlineId}`);
      resolve(true);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Get transactions by status
 * @param {string} status - 'pending' | 'syncing' | 'synced' | 'conflict' | 'failed'
 */
export async function getTransactionsByStatus(status) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.PENDING_TRANSACTIONS, 'readonly');
    const store = txn.objectStore(STORES.PENDING_TRANSACTIONS);
    const index = store.index('syncStatus');
    const request = index.getAll(status);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Count a given list of transactions by status (pure, no DB access) —
 * shared by getTransactionCounts() (global) and useOfflineSync (per-branch)
 * so the two never drift out of sync on which statuses map to which key.
 */
export function countTransactionsByStatus(transactions) {
  const counts = {
    pending: 0,
    syncing: 0,
    synced: 0,
    conflict: 0,
    failed: 0,
    needsReview: 0,
    total: transactions.length
  };

  // Explicit map from DB status strings to counts keys
  const STATUS_MAP = {
    pending: 'pending',
    syncing: 'syncing',
    synced: 'synced',
    conflict: 'conflict',
    failed: 'failed',
    needs_review: 'needsReview'
  };

  for (const txn of transactions) {
    const key = STATUS_MAP[txn.syncStatus];
    if (key) {
      counts[key]++;
    }
  }

  return counts;
}

/**
 * Get count of transactions by status (all branches)
 */
export async function getTransactionCounts() {
  const all = await getAllTransactions();
  return countTransactionsByStatus(all);
}

/**
 * Check for stuck 'syncing' transactions and reset them
 * Called on app init to recover from browser close during sync
 */
export async function recoverStuckTransactions() {
  const syncing = await getTransactionsByStatus('syncing');

  for (const txn of syncing) {
    console.log(`[OfflineDB] Recovering stuck transaction: ${txn.offlineId}`);
    await updateTransaction(txn.offlineId, {
      syncStatus: 'pending',
      errorMessage: 'Sync interrupted - retrying'
    });
  }

  if (syncing.length > 0) {
    console.log(`[OfflineDB] Recovered ${syncing.length} stuck transactions`);
  }

  return syncing.length;
}

// ============================================
// SYNC HISTORY OPERATIONS
// ============================================

/**
 * Log a successful sync to history
 */
export async function logSyncHistory(offlineId, serverResponse) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.SYNC_HISTORY, 'readwrite');
    const store = txn.objectStore(STORES.SYNC_HISTORY);

    const record = {
      offlineId,
      syncedAt: Date.now(),
      serverReceiptId: serverResponse?.receiptId,
      serverTransactionId: serverResponse?._id,
      serverResponse
    };

    const request = store.add(record);

    request.onsuccess = () => {
      resolve(record);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Get sync history for a transaction
 */
export async function getSyncHistory(offlineId) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.SYNC_HISTORY, 'readonly');
    const store = txn.objectStore(STORES.SYNC_HISTORY);
    const index = store.index('offlineId');
    const request = index.getAll(offlineId);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// ============================================
// CONFLICTS OPERATIONS
// ============================================

/**
 * Log a conflict for admin review
 */
export async function logConflict(offlineId, conflictType, serverResponse, transaction) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.CONFLICTS, 'readwrite');
    const store = txn.objectStore(STORES.CONFLICTS);

    const record = {
      offlineId,
      conflictType,
      serverResponse,
      transaction,
      detectedAt: Date.now(),
      resolvedAt: null,
      resolution: null,
      notes: null
    };

    const request = store.add(record);

    request.onsuccess = () => {
      console.log(`[OfflineDB] Logged conflict: ${offlineId} - ${conflictType}`);
      resolve(record);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * Get all unresolved conflicts
 */
export async function getUnresolvedConflicts() {
  const database = await getDb();

  const allLoggedConflicts = await new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.CONFLICTS, 'readonly');
    const store = txn.objectStore(STORES.CONFLICTS);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result.filter(c => !c.resolvedAt));
    };

    request.onerror = () => {
      reject(request.error);
    };
  });

  // logConflict() บันทึกทุก conflict ที่เจอไว้ถาวร รวมถึงประเภทที่ระบบ auto-resolve ให้เอง
  // เงียบๆ (ALREADY_CHECKED_OUT/TABLE_ALREADY_CLOSED ใน handleConflict()) แต่ auto-resolve
  // นั้นอัปเดตแค่ syncStatus ของ transaction เอง ไม่เคยย้อนมาปิด resolvedAt ของ log รายการนี้
  // เลย ทำให้รายการที่จัดการเรียบร้อยแล้วค้างโชว์ในหน้า "ต้องตรวจสอบ" ถาวร (ไม่ตรงกับตัวเลข
  // สรุปที่นับจาก transaction.syncStatus==='conflict' จริงๆ) — เช็คสถานะปัจจุบันของ
  // transaction ซ้ำอีกชั้นตรงนี้แทน ไม่ต้องไล่แก้ทุกจุดที่เรียก auto-resolve
  const allTransactions = await getAllTransactions();
  const txnByOfflineId = new Map(allTransactions.map(t => [t.offlineId, t]));

  return allLoggedConflicts.filter(c => txnByOfflineId.get(c.offlineId)?.syncStatus === 'conflict');
}

/**
 * Resolve a conflict
 */
export async function resolveConflict(conflictId, resolution, notes) {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const txn = database.transaction(STORES.CONFLICTS, 'readwrite');
    const store = txn.objectStore(STORES.CONFLICTS);
    const getRequest = store.get(conflictId);

    getRequest.onsuccess = () => {
      const conflict = getRequest.result;
      if (!conflict) {
        reject(new Error(`Conflict not found: ${conflictId}`));
        return;
      }

      const updated = {
        ...conflict,
        resolvedAt: Date.now(),
        resolution,
        notes
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => {
        console.log(`[OfflineDB] Resolved conflict: ${conflictId}`);
        resolve(updated);
      };

      putRequest.onerror = () => {
        reject(putRequest.error);
      };
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Clear all synced transactions (cleanup)
 */
export async function clearSyncedTransactions() {
  const synced = await getTransactionsByStatus('synced');

  for (const txn of synced) {
    await deleteTransaction(txn.offlineId);
  }

  console.log(`[OfflineDB] Cleared ${synced.length} synced transactions`);
  return synced.length;
}

/**
 * Check if a table has a pending transaction
 */
export async function hasPendingTransactionForTable(tableCode) {
  const pending = await getPendingTransactions();
  return pending.some(txn => txn.tableCode === tableCode);
}

/**
 * Export for debugging
 */
export const offlineDb = {
  initOfflineDb,
  addPendingTransaction,
  getPendingTransactions,
  getAllTransactions,
  getTransactionById,
  updateTransaction,
  deleteTransaction,
  getTransactionsByStatus,
  getTransactionCounts,
  countTransactionsByStatus,
  recoverStuckTransactions,
  logSyncHistory,
  getSyncHistory,
  logConflict,
  getUnresolvedConflicts,
  resolveConflict,
  clearSyncedTransactions,
  hasPendingTransactionForTable
};

export default offlineDb;
